import { useState } from 'react';
import { Link } from 'react-router-dom';
import { SEO } from '../components/SEO';
import { useAuth } from '../contexts/AuthContext';
import './ContactPage.css';
import { API_BASE } from '../config';

export function ContactPage() {
  const { user } = useAuth();
  const [name, setName] = useState(user?.displayName || '');
  const [email, setEmail] = useState(user?.email || '');
  const [subject, setSubject] = useState('general');
  const [message, setMessage] = useState('');
  const [submitted, setSubmitted] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [loading, setLoading] = useState(false);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setError(null);

    if (!name.trim() || !email.trim() || !message.trim()) {
      setError('Please fill in all required fields.');
      return;
    }

    if (message.trim().length < 10) {
      setError('Message must be at least 10 characters.');
      return;
    }

    setLoading(true);

    try {
      const res = await fetch(`${API_BASE}/v1/feedback`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          name: name.trim(),
          email: email.trim(),
          category: subject,
          message: message.trim(),
        }),
      });

      if (!res.ok) {
        const json = await res.json().catch(() => ({}));
        throw new Error(json.error?.message || 'Failed to send message');
      }

      setSubmitted(true);
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Something went wrong. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="contact-page">
      <SEO
        title="Contact Us - Hygh Vybe"
        description="Get in touch with the Hygh Vybe team. Questions about THC beverages, your account, or partnership opportunities."
        canonical="/contact"
      />

      <header className="contact-page__hero">
        <h1>Contact Us</h1>
        <p className="contact-page__subtitle">
          Have a question, feedback, or partnership inquiry? We'd love to hear from you.
        </p>
      </header>

      {submitted ? (
        <section className="contact-page__success">
          <div className="contact-page__success-icon">✓</div>
          <h2>Message Sent</h2>
          <p>Thanks for reaching out. We'll get back to you within 1-2 business days.</p>
          <Link to="/" className="contact-page__cta">
            Back to Home
          </Link>
        </section>
      ) : (
        <section className="contact-page__form-section">
          <form className="contact-page__form" onSubmit={handleSubmit}>
            {error && (
              <div className="contact-page__error" role="alert">
                {error}
              </div>
            )}

            <div className="contact-page__field">
              <label htmlFor="contact-name">Name *</label>
              <input
                id="contact-name"
                type="text"
                value={name}
                onChange={(e) => setName(e.target.value)}
                placeholder="Your name"
                disabled={loading}
              />
            </div>

            <div className="contact-page__field">
              <label htmlFor="contact-email">Email *</label>
              <input
                id="contact-email"
                type="email"
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                placeholder="you@example.com"
                disabled={loading}
              />
            </div>

            <div className="contact-page__field">
              <label htmlFor="contact-subject">What's this about?</label>
              <select
                id="contact-subject"
                value={subject}
                onChange={(e) => setSubject(e.target.value)}
                disabled={loading}
              >
                <option value="general">General Question</option>
                <option value="account">Account / Login Issue</option>
                <option value="review">Review or Rating Issue</option>
                <option value="brand">Brand Partnership</option>
                <option value="bug">Bug Report</option>
                <option value="other">Other</option>
              </select>
            </div>

            <div className="contact-page__field">
              <label htmlFor="contact-message">Message *</label>
              <textarea
                id="contact-message"
                value={message}
                onChange={(e) => setMessage(e.target.value)}
                placeholder="How can we help?"
                rows={5}
                disabled={loading}
              />
            </div>

            <button
              type="submit"
              className="contact-page__submit"
              disabled={loading}
            >
              {loading ? 'Sending...' : 'Send Message'}
            </button>
          </form>

          <aside className="contact-page__info">
            <h3>Other Ways to Reach Us</h3>
            <div className="contact-page__info-items">
              <div className="contact-page__info-item">
                <span className="contact-page__info-icon">⏱</span>
                <div>
                  <strong>Response Time</strong>
                  <p>We typically respond within 1-2 business days.</p>
                </div>
              </div>
              <div className="contact-page__info-item">
                <span className="contact-page__info-icon">🤝</span>
                <div>
                  <strong>Brand Partnerships</strong>
                  <p>Interested in getting your brand listed? Select "Brand Partnership" above and tell us about your products.</p>
                </div>
              </div>
              <div className="contact-page__info-item">
                <span className="contact-page__info-icon">🐛</span>
                <div>
                  <strong>Found a Bug?</strong>
                  <p>Select "Bug Report" and include what you were doing when the issue occurred. Screenshots help!</p>
                </div>
              </div>
            </div>
          </aside>
        </section>
      )}
    </div>
  );
}
